import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommerceService, ReturnedSurveyPayload, ToastService } from '@common/services';

import {
    isValidSurveyOtherText,
    PostPaymentSurveyStep,
    shuffleSurveyOptions,
    SURVEY_OTHER_VALUE,
    SURVEY_P1_OPTIONS,
    SURVEY_P1A_OPTIONS,
    SURVEY_P1B_OPTIONS,
    SURVEY_P1C_OPTIONS,
    SURVEY_P2_OPTIONS,
    SURVEY_P2A_OPTIONS,
    SURVEY_P2B_OPTIONS,
    SURVEY_P3_OPTIONS,
    SURVEY_STEP_TITLES,
    SurveyOption,
} from './post-payment-survey.config';

@Component({
    selector: 'fina-post-payment-survey',
    templateUrl: './post-payment-survey.component.html',
    styleUrls: ['./post-payment-survey.component.scss'],
})
export class PostPaymentSurveyComponent implements OnInit {
    @Output() completed = new EventEmitter<void>();

    wizardStep: PostPaymentSurveyStep = 'P1';
    /** Historial de pasos visitados para poder volver. */
    private stepHistory: PostPaymentSurveyStep[] = [];
    whyLeft: string | null = null;
    whyLeftFollowUp: string | null = null;
    whyLeftOther = '';
    whyReturned: string | null = null;
    whyReturnedFollowUp: string | null = null;
    whyReturnedOther = '';
    communicationSource: string | null = null;
    submitting = false;
    otherTouched = false;

    p1Options: SurveyOption[] = [];
    p1aOptions: SurveyOption[] = [];
    p1bOptions: SurveyOption[] = [];
    p1cOptions: SurveyOption[] = [];
    p2Options: SurveyOption[] = [];
    p2aOptions: SurveyOption[] = [];
    p2bOptions: SurveyOption[] = [...SURVEY_P2B_OPTIONS];
    p3Options: SurveyOption[] = [];

    readonly stepTitles = SURVEY_STEP_TITLES;

    constructor(private commerceService: CommerceService, private toastService: ToastService) {}

    ngOnInit(): void {
        this.initSurvey();
    }

    get currentOptions(): SurveyOption[] {
        switch (this.wizardStep) {
            case 'P1':
                return this.p1Options;
            case 'P1a':
                return this.p1aOptions;
            case 'P1b':
                return this.p1bOptions;
            case 'P1c':
                return this.p1cOptions;
            case 'P2':
                return this.p2Options;
            case 'P2a':
                return this.p2aOptions;
            case 'P2b':
                return this.p2bOptions;
            case 'P3':
                return this.p3Options;
            default:
                return [];
        }
    }

    get currentSelection(): string | null {
        switch (this.wizardStep) {
            case 'P1':
                return this.whyLeft;
            case 'P1a':
            case 'P1b':
            case 'P1c':
                return this.whyLeftFollowUp;
            case 'P2':
                return this.whyReturned;
            case 'P2a':
            case 'P2b':
                return this.whyReturnedFollowUp;
            case 'P3':
                return this.communicationSource;
            default:
                return null;
        }
    }

    get showsOtherInput(): boolean {
        return (this.wizardStep === 'P1c' && this.whyLeftFollowUp === SURVEY_OTHER_VALUE) || (this.wizardStep === 'P2a' && this.whyReturnedFollowUp === SURVEY_OTHER_VALUE);
    }

    get otherText(): string {
        return this.wizardStep === 'P1c' ? this.whyLeftOther : this.whyReturnedOther;
    }

    get otherInvalid(): boolean {
        return this.showsOtherInput && this.otherTouched && !isValidSurveyOtherText(this.otherText);
    }

    get isFinalStep(): boolean {
        return this.wizardStep === 'P3';
    }

    get canGoBack(): boolean {
        return this.stepHistory.length > 0 && !this.submitting;
    }

    onOptionSelect(value: string): void {
        switch (this.wizardStep) {
            case 'P1':
                this.whyLeft = value;
                this.whyLeftFollowUp = null;
                this.whyLeftOther = '';
                break;
            case 'P1a':
            case 'P1b':
            case 'P1c':
                this.whyLeftFollowUp = value;
                if (value !== SURVEY_OTHER_VALUE) {
                    this.whyLeftOther = '';
                    this.otherTouched = false;
                }
                break;
            case 'P2':
                this.whyReturned = value;
                this.whyReturnedFollowUp = null;
                this.whyReturnedOther = '';
                break;
            case 'P2a':
            case 'P2b':
                this.whyReturnedFollowUp = value;
                if (value !== SURVEY_OTHER_VALUE) {
                    this.whyReturnedOther = '';
                    this.otherTouched = false;
                }
                break;
            case 'P3':
                this.communicationSource = value;
                break;
        }
    }

    onOtherInput(value: string): void {
        this.otherTouched = true;
        if (this.wizardStep === 'P1c') {
            this.whyLeftOther = value;
        } else if (this.wizardStep === 'P2a') {
            this.whyReturnedOther = value;
        }
    }

    canAdvance(): boolean {
        const selection = this.currentSelection;
        if (!selection) {
            return false;
        }
        if (this.showsOtherInput) {
            return isValidSurveyOtherText(this.otherText);
        }
        return true;
    }

    advance(): void {
        if (!this.canAdvance()) {
            if (this.showsOtherInput) {
                this.otherTouched = true;
            }
            return;
        }
        if (this.wizardStep === 'P3') {
            this.submit();
            return;
        }
        this.otherTouched = false;
        this.stepHistory.push(this.wizardStep);
        this.wizardStep = this.resolveNextStep();
    }

    goBack(): void {
        if (!this.canGoBack) {
            return;
        }
        const previousStep = this.stepHistory.pop();
        if (!previousStep) {
            return;
        }
        this.otherTouched = false;
        this.wizardStep = previousStep;
    }

    private initSurvey(): void {
        this.p1Options = shuffleSurveyOptions(SURVEY_P1_OPTIONS);
        this.p1aOptions = shuffleSurveyOptions(SURVEY_P1A_OPTIONS);
        this.p1bOptions = shuffleSurveyOptions(SURVEY_P1B_OPTIONS);
        this.p1cOptions = shuffleSurveyOptions(SURVEY_P1C_OPTIONS);
        this.p2Options = shuffleSurveyOptions(SURVEY_P2_OPTIONS);
        this.p2aOptions = shuffleSurveyOptions(SURVEY_P2A_OPTIONS);
        this.p2bOptions = [...SURVEY_P2B_OPTIONS];
        this.p3Options = shuffleSurveyOptions(SURVEY_P3_OPTIONS);
        this.wizardStep = 'P1';
        this.stepHistory = [];
        this.whyLeft = null;
        this.whyLeftFollowUp = null;
        this.whyLeftOther = '';
        this.whyReturned = null;
        this.whyReturnedFollowUp = null;
        this.whyReturnedOther = '';
        this.communicationSource = null;
        this.submitting = false;
        this.otherTouched = false;
    }

    private resolveNextStep(): PostPaymentSurveyStep {
        switch (this.wizardStep) {
            case 'P1':
                if (this.whyLeft === 'lack_of_time') {
                    return 'P1a';
                }
                if (this.whyLeft === 'did_not_know_how') {
                    return 'P1b';
                }
                if (this.whyLeft === 'features') {
                    return 'P1c';
                }
                return 'P2';
            case 'P1a':
            case 'P1b':
            case 'P1c':
                return 'P2';
            case 'P2':
                if (this.whyReturned === 'someone_explained') {
                    return 'P2b';
                }
                if (this.whyReturned === 'new_feature') {
                    return 'P2a';
                }
                return 'P3';
            case 'P2a':
            case 'P2b':
                return 'P3';
            default:
                return 'P3';
        }
    }

    private submit(): void {
        if (this.wizardStep !== 'P3' || !this.canAdvance() || !this.whyLeft || !this.whyReturned || !this.communicationSource) {
            return;
        }
        this.submitting = true;
        const payload: ReturnedSurveyPayload = {
            whyLeft: this.whyLeft,
            whyReturned: this.whyReturned,
            whyLeftFollowUp: this.whyLeftFollowUp,
            whyLeftOther: this.whyLeftFollowUp === SURVEY_OTHER_VALUE ? this.whyLeftOther.trim() : null,
            whyReturnedFollowUp: this.whyReturnedFollowUp,
            whyReturnedOther: this.whyReturnedFollowUp === SURVEY_OTHER_VALUE ? this.whyReturnedOther.trim() : null,
            communicationSource: this.communicationSource,
        };
        this.commerceService.commerceReturnedSurvey$(this.commerceService.commerce.tenantId, payload).subscribe({
            next: (res: any) => {
                this.toastService.success(res.title, res.message);
                this.submitting = false;
                this.completed.emit();
            },
            error: (err: any) => {
                this.submitting = false;
                this.toastService.error(err.error.title, err.error.message);
            },
        });
    }
}
